import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Form, Input } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useStocksServiceConfirmInternalEntryEndpoint,
  useStocksServiceGetEntryEndpointKey,
  useStocksServiceGetInternalEntryEndpoint,
  useStocksServiceRejectInternalEntryEndpoint
} from 'src/api/queries'
import {
  ApiError,
  InternalInventoryDocumentState,
  Permissions
} from 'src/api/requests'
import {
  ConfirmModal,
  LoadingScreen,
  message,
  PageBreadcrumbs,
  SaveConfirm
} from 'src/components'
import { useScrollToFirstError, useUser, ValidationError } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

import { LocalInputConfirmedDetails } from '../../components'

export const LocalStockInputsDetailsPage = () => {
  const { id } = useParams()

  const { hasPermission } = useUser()
  const [isReject, setIsReject] = useState(false)

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const { onScrollToError } = useScrollToFirstError(form)

  const navigate = useNavigate()
  const { t } = useLocales()

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Entry_${id}`
  })

  const { data, isLoading, refetch } = useStocksServiceGetInternalEntryEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    {
      enabled: !!id
    }
  )

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetEntryEndpointKey, { id: String(id) }]
    })
    refetch()
  }

  const { mutate: acceptMutation } =
    useStocksServiceConfirmInternalEntryEndpoint({
      onSuccess: () => {
        onMutationSuccess()
        message.success(t('stock_administration.accepted_success'))
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const { mutate: rejectMutation } =
    useStocksServiceRejectInternalEntryEndpoint({
      onSuccess: () => {
        onMutationSuccess()
        message.success(t('stock_administration.rejected_success'))
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const onBackClick = () => {
    form.resetFields()
    navigate(paths.stockLocal.inputs.root)
  }

  const isNew =
    data?.state === InternalInventoryDocumentState.NEW ||
    data?.state === InternalInventoryDocumentState.WAITING

  const isStockAdmin = hasPermission(
    Permissions.STOCKS_INTERNAL_MENU_ACCESS_ISSUANCE
  )

  useEffect(() => {
    if (id && data) {
      const existingFileIds = data?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      form.setFieldsValue({
        invoiceDate: toDayjs(data?.documentDate),
        medicine: data?.items?.map((item) => ({
          barCode: item?.barCode,
          name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
          productName: item?.producer?.name,
          producerName: item?.product?.name,
          expirationDate: toDayjs(item?.expirationDate)?.format(
            yearMonthDaySlash
          ),
          unit: item?.productForm?.name,
          unitPrice: (item?.unitPrice ?? 0).toFixed(2),
          priceTotal: ((item?.quantity ?? 0) * (item?.unitPrice ?? 0)).toFixed(
            2
          ),
          quantity: `${item?.quantity} ${item?.productForm?.name}`
        })),
        docs: existingFileIds?.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        })),
        comment: data?.comment
      })
    }
  }, [data, id])

  const onAccept = async () => {
    try {
      const values = await form.validateFields()

      acceptMutation({
        id: String(data?.id),
        requestBody: {
          deliveryDate: dayjs(values?.deliveryDate).format(yearMonthDaySlash),
          comment: values.commentNew
        }
      })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  const onReject = async () => {
    form.setFieldsValue({
      deliveryDate: dayjs(),
      comment: form.getFieldValue('commentReject') ?? ' '
    })

    const values = await form.validateFields()

    rejectMutation({
      id: String(data?.id),
      requestBody: {
        comment: values.commentNew
      }
    })
    setIsReject(false)
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={`${t('stock.stock_entry')}: ${String(data?.code)}`}
      items={[
        {
          title: t('menu.consumable_medicaments'),
          path: paths.stockLocal.inputs.root
        },
        {
          title: t('stock.inputs'),
          path: paths.stockLocal.inputs.root
        },
        {
          title: `${t('stock.stock_entry')}: ${String(data?.code)}`
        }
      ]}
    >
      <Flex vertical gap={24}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>

          <Flex gap={16}>
            {!data && (
              <Button onClick={onBackClick}>{t('common.cancel')}</Button>
            )}

            <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
              {t('stock_applications.print')}
            </Button>

            {isNew && !isStockAdmin && (
              <>
                <SaveConfirm
                  title={`${t('stock_administration.accept')}?`}
                  placement="leftBottom"
                  onConfirm={onAccept}
                >
                  <Button type="primary">
                    {t('stock_administration.accept')}
                  </Button>
                </SaveConfirm>

                <SaveConfirm
                  title={`${t('stock_administration.reject')}?`}
                  placement="leftBottom"
                  onConfirm={() => setIsReject(true)}
                >
                  <Button danger>{t('stock_administration.reject')}</Button>
                </SaveConfirm>
              </>
            )}
          </Flex>
        </Flex>

        <Form
          layout="vertical"
          form={form}
          requiredMark={false}
          disabled={!!data && !isReject}
          scrollToFirstError
        >
          <div ref={printableRef}>
            <LocalInputConfirmedDetails item={data} form={form} />
          </div>

          <ConfirmModal
            open={isReject}
            title={`${t('stock_administration.reject')}?`}
            onCancel={() => setIsReject(false)}
            onOk={onReject}
            okButtonProps={{ danger: true }}
          >
            <Card>
              <Form.Item name="commentNew">
                <Input.TextArea
                  rows={5}
                  placeholder={t('common.leave_comment')}
                />
              </Form.Item>
            </Card>
          </ConfirmModal>
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
